/**
 * MerchantPortal.jsx
 * ESDRAS MERCHANT MARKETPLACE — Vendor Auth Modal
 * Handles: Merchant Login, Merchant Sign Up, Google Auth, Terms Agreement
 * Accessible via: "SELL ON ESDRAS" button on homepage
 * Super Admins: Bypass approval → redirected to /admin/merchant
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  updateProfile,
  onAuthStateChanged,
  signOut,
} from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { auth, db, storage, googleProvider } from '../../firebase';
import { toast } from 'react-hot-toast';

// ─── CONSTANTS ───────────────────────────────────────────────────────────────
const GOLD        = '#B8860B';
const NAVY        = '#001F3F';
const DEEP_BLACK  = '#050a0f';
const CARD_BG     = '#0a0f14';
const BORDER      = '#1a2631';
const SUPER_ADMINS = ['FFXDokpj5IXR3SFSvJyDu70LRp73', 'GbjKMdeVEmTgMefcU1cEHsEOstn1'];

// Nigerian bank list (CBN codes) — common banks
const NIGERIAN_BANKS = [
  { name: 'Access Bank',            code: '044' },
  { name: 'First Bank Nigeria',     code: '011' },
  { name: 'Guaranty Trust Bank',    code: '058' },
  { name: 'United Bank for Africa', code: '033' },
  { name: 'Zenith Bank',            code: '057' },
  { name: 'Fidelity Bank',          code: '070' },
  { name: 'Sterling Bank',          code: '232' },
  { name: 'Stanbic IBTC Bank',      code: '221' },
  { name: 'Union Bank',             code: '032' },
  { name: 'Ecobank',                code: '050' },
  { name: 'Heritage Bank',          code: '030' },
  { name: 'Keystone Bank',          code: '082' },
  { name: 'Polaris Bank',           code: '076' },
  { name: 'Providus Bank',          code: '101' },
  { name: 'Wema Bank',              code: '035' },
  { name: 'OPay',                   code: '999992' },
  { name: 'Kuda Bank',              code: '090267' },
  { name: 'PalmPay',                code: '999991' },
  { name: 'Moniepoint',             code: '090405' },
  { name: 'Others',                 code: '999' },
];

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────
export default function MerchantPortal({ onClose }) {
  const navigate = useNavigate();

  const [tab,           setTab]           = useState('login');
  const [loading,       setLoading]       = useState(false);
  const [showTerms,     setShowTerms]     = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [portalState,   setPortalState]   = useState(null); // null | 'pending' | 'rejected'
  const [rejectionReason, setRejectionReason] = useState('');

  // Login fields
  const [loginEmail,    setLoginEmail]    = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  // Signup fields
  const [signup, setSignup] = useState({
    contactName:       '',
    businessName:      '',
    email:             '',
    password:          '',
    confirmPassword:   '',
    phone:             '',
    address:           '',
    city:              '',
    merchantType:      'individual',
    categories:        [],
    gender:            '',
    cacNumber:         '',
    bankAccountNumber: '',
    bankCode:          '',
    bankName:          '',
  });
  const [regDoc,    setRegDoc]    = useState(null);
  const [logoFile,  setLogoFile]  = useState(null);
  const [uploadProgress, setUploadProgress] = useState('');

  const setField = (key, val) => setSignup(p => ({ ...p, [key]: val }));
  const toggleCat = (cat) => setSignup(p => ({
    ...p,
    categories: p.categories.includes(cat)
      ? p.categories.filter(c => c !== cat)
      : [...p.categories, cat],
  }));

  // ── Auth State Observer ──────────────────────────────────────────────────
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) return;
      if (SUPER_ADMINS.includes(user.uid)) {
        onClose?.();
        navigate('/admin/merchant');
        return;
      }
      const snap = await getDoc(doc(db, 'merchants', user.uid)).catch(() => null);
      if (!snap?.exists()) return;
      const d = snap.data();
      if (d.status === 'approved') { onClose?.(); navigate('/merchant/dashboard'); }
      else if (d.status === 'pending_approval') setPortalState('pending');
      else if (d.status === 'rejected') {
        setPortalState('rejected');
        setRejectionReason(d.rejectionReason || '');
      }
    });
    return () => unsub();
  }, [navigate, onClose]);

  // ── Login ────────────────────────────────────────────────────────────────
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { user } = await signInWithEmailAndPassword(auth, loginEmail, loginPassword);
      if (SUPER_ADMINS.includes(user.uid)) {
        onClose?.(); navigate('/admin/merchant'); return;
      }
      const snap = await getDoc(doc(db, 'merchants', user.uid));
      if (!snap.exists()) {
        await signOut(auth);
        toast.error('No merchant account found. Please register.');
        setLoading(false);
        return;
      }
      const d = snap.data();
      if (d.status === 'approved')          { onClose?.(); navigate('/merchant/dashboard'); }
      else if (d.status === 'pending_approval') setPortalState('pending');
      else if (d.status === 'rejected')         { setPortalState('rejected'); setRejectionReason(d.rejectionReason || ''); }
    } catch (err) {
      toast.error(err.code === 'auth/invalid-credential' ? 'Invalid email or password.' : err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Sign Up ──────────────────────────────────────────────────────────────
  const handleSignup = async (e) => {
    e.preventDefault();
    if (!termsAccepted)                      { setShowTerms(true); return; }
    if (signup.categories.length === 0)      { toast.error('Select at least one product category.'); return; }
    if (signup.password !== signup.confirmPassword) { toast.error('Passwords do not match.'); return; }
    if (!regDoc)                             { toast.error('Please upload your business registration document.'); return; }

    setLoading(true);
    try {
      const { user } = await createUserWithEmailAndPassword(auth, signup.email.toLowerCase().trim(), signup.password);
      await updateProfile(user, { displayName: signup.contactName.trim() });

      setUploadProgress('Uploading documents...');
      let docUrl  = '';
      let logoUrl = '';

      if (regDoc) {
        const docRef = ref(storage, `merchant_docs/${user.uid}/reg_${Date.now()}`);
        await uploadBytes(docRef, regDoc);
        docUrl = await getDownloadURL(docRef);
      }
      if (logoFile) {
        const lRef = ref(storage, `merchant_profiles/${user.uid}/logo_${Date.now()}`);
        await uploadBytes(lRef, logoFile);
        logoUrl = await getDownloadURL(lRef);
      }

      setUploadProgress('Creating your merchant profile...');
      await setDoc(doc(db, 'merchants', user.uid), {
        uid:                 user.uid,
        email:               signup.email.toLowerCase().trim(),
        contactName:         signup.contactName.trim(),
        businessName:        signup.businessName.trim(),
        merchantType:        signup.merchantType,
        phone:               signup.phone.trim(),
        address:             signup.address.trim(),
        city:                signup.city.trim(),
        categories:          signup.categories,
        gender:              signup.gender,
        cacNumber:           signup.cacNumber.trim(),
        businessRegDocUrl:   docUrl,
        logoUrl:             logoUrl,
        profileImageUrl:     logoUrl,
        bankAccountNumber:   signup.bankAccountNumber.trim(),
        bankCode:            signup.bankCode.trim(),
        bankName:            signup.bankName.trim(),
        status:              'pending_approval',
        rejectionReason:     null,
        approvedBy:          null,
        approvedAt:          null,
        monnifySubAccountCode: null,
        termsAccepted:       true,
        termsVersion:        'merchant_2026_v1',
        shareableProfileLink:'',
        inviteSent:          false,
        verifiedAt:          null,
        totalProductsLive:   0,
        totalOrdersCount:    0,
        totalRevenue:        0,
        createdAt:           serverTimestamp(),
        updatedAt:           serverTimestamp(),
      });

      setPortalState('pending');
      toast.success(`Application submitted! We'll review your profile shortly.`);
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
      setUploadProgress('');
    }
  };

  // ── Google Auth ──────────────────────────────────────────────────────────
  const handleGoogle = async (isSignup = false) => {
    if (isSignup && !termsAccepted) { setShowTerms(true); return; }
    setLoading(true);
    try {
      const { user } = await signInWithPopup(auth, googleProvider);
      if (SUPER_ADMINS.includes(user.uid)) { onClose?.(); navigate('/admin/merchant'); return; }
      const snap = await getDoc(doc(db, 'merchants', user.uid));
      if (snap.exists()) {
        const d = snap.data();
        if (d.status === 'approved') { onClose?.(); navigate('/merchant/dashboard'); }
        else if (d.status === 'pending_approval') setPortalState('pending');
        else { setPortalState('rejected'); setRejectionReason(d.rejectionReason || ''); }
      } else {
        setField('email', user.email || '');
        setField('contactName', user.displayName || '');
        setTab('signup');
        toast('Complete your merchant profile to continue.', { icon: '📋' });
      }
    } catch (err) {
      toast.error('Google sign-in failed.');
    } finally {
      setLoading(false);
    }
  };

  // ── BANK SELECT HELPER ───────────────────────────────────────────────────
  const handleBankSelect = (e) => {
    const selected = NIGERIAN_BANKS.find(b => b.code === e.target.value);
    if (selected) { setField('bankCode', selected.code); setField('bankName', selected.name); }
  };

  // ════════════════════════════════════════════════════════
  // RENDER — PENDING STATE
  // ════════════════════════════════════════════════════════
  if (portalState === 'pending') return (
    <Overlay onClose={onClose}>
      <div style={{ textAlign: 'center', padding: '10px' }}>
        <div style={{ fontSize: '3.5rem', marginBottom: '15px' }}>⏳</div>
        <h2 style={S.goldTitle}>APPLICATION RECEIVED</h2>
        <p style={S.mutedText}>
          Your merchant profile is under review by the Esdras team.<br /><br />
          You'll receive an email once approved — usually within 24–48 hours.
          Check your inbox (and spam folder) for updates.
        </p>
        <button onClick={onClose} style={S.goldBtn}>GOT IT</button>
      </div>
    </Overlay>
  );

  // ════════════════════════════════════════════════════════
  // RENDER — REJECTED STATE
  // ════════════════════════════════════════════════════════
  if (portalState === 'rejected') return (
    <Overlay onClose={onClose}>
      <div style={{ textAlign: 'center', padding: '10px' }}>
        <div style={{ fontSize: '3.5rem', marginBottom: '15px' }}>❌</div>
        <h2 style={{ ...S.goldTitle, color: '#ff4d4d' }}>APPLICATION DECLINED</h2>
        {rejectionReason && (
          <div style={{ background: 'rgba(255,77,77,0.08)', border: '1px solid #ff4d4d44', borderRadius: '12px', padding: '15px', margin: '15px 0' }}>
            <p style={{ color: '#ff8888', fontSize: '0.88rem', margin: 0, lineHeight: '1.5' }}>
              <strong style={{ color: '#ff4d4d' }}>Reason:</strong><br />{rejectionReason}
            </p>
          </div>
        )}
        <p style={S.mutedText}>Please contact <span style={{ color: GOLD }}>esdrastechnologiesinc@gmail.com</span> if you believe this is an error.</p>
        <button onClick={onClose} style={S.goldBtn}>CLOSE</button>
      </div>
    </Overlay>
  );

  // ════════════════════════════════════════════════════════
  // RENDER — MAIN PORTAL
  // ════════════════════════════════════════════════════════
  return (
    <>
      <Overlay onClose={onClose}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '22px' }}>
          <img src="/icons/esdras-logo.png" alt="Esdras" style={{ width: '46px', marginBottom: '8px' }}
            onError={e => { e.target.style.display = 'none'; }} />
          <h2 style={S.goldTitle}>MERCHANT PORTAL</h2>
          <p style={{ color: '#444', fontSize: '0.65rem', letterSpacing: '3px', margin: '3px 0 0' }}>
            SELL ON ESDRAS
          </p>
        </div>

        {/* Tab Switcher */}
        <div style={S.tabRow}>
          {['login', 'signup'].map(t => (
            <button key={t} onClick={() => setTab(t)}
              style={tab === t ? S.tabActive : S.tabInactive}>
              {t === 'login' ? 'SIGN IN' : 'REGISTER'}
            </button>
          ))}
        </div>

        {/* ── LOGIN FORM ── */}
        {tab === 'login' && (
          <form onSubmit={handleLogin} style={S.form}>
            <input type="email" placeholder="Business Email" value={loginEmail}
              onChange={e => setLoginEmail(e.target.value)} style={S.input} required autoComplete="email" />
            <input type="password" placeholder="Password" value={loginPassword}
              onChange={e => setLoginPassword(e.target.value)} style={S.input} required autoComplete="current-password" />
            <button type="submit" style={S.goldBtn} disabled={loading}>
              {loading ? 'SIGNING IN...' : 'ENTER PORTAL'}
            </button>
            <Divider />
            <button type="button" onClick={() => handleGoogle(false)} style={S.googleBtn}>
              <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="G" style={{ width: '18px', marginRight: '8px' }} />
              Continue with Google
            </button>
            <p style={{ color: '#444', fontSize: '0.75rem', textAlign: 'center', marginTop: '12px' }}>
              New merchant?{' '}
              <span onClick={() => setTab('signup')} style={{ color: GOLD, cursor: 'pointer' }}>Register here</span>
            </p>
          </form>
        )}

        {/* ── SIGNUP FORM ── */}
        {tab === 'signup' && (
          <form onSubmit={handleSignup} style={S.form}>

            {/* Merchant Type */}
            <SectionLabel>MERCHANT TYPE</SectionLabel>
            <div style={S.toggleRow}>
              {[['individual', 'INDIVIDUAL'], ['institutional', 'BUSINESS / BRAND']].map(([val, label]) => (
                <button type="button" key={val} onClick={() => setField('merchantType', val)}
                  style={signup.merchantType === val ? S.toggleActive : S.toggleInactive}>
                  {label}
                </button>
              ))}
            </div>

            {/* Categories */}
            <SectionLabel>PRODUCT CATEGORIES <span style={{ color: '#ff4d4d' }}>*</span></SectionLabel>
            <div style={S.toggleRow}>
              {[['hair_care', '💆 HAIR CARE'], ['skin_care', '✨ SKIN CARE']].map(([cat, label]) => (
                <button type="button" key={cat} onClick={() => toggleCat(cat)}
                  style={signup.categories.includes(cat) ? S.toggleActive : S.toggleInactive}>
                  {label}
                </button>
              ))}
            </div>
            {signup.categories.length === 0 && (
              <p style={{ color: '#ff6644', fontSize: '0.72rem', margin: '-4px 0 4px' }}>Select at least one category</p>
            )}

            {/* Contact Details */}
            <SectionLabel>YOUR DETAILS</SectionLabel>
            <input placeholder="Full Contact Name" style={S.input} required
              value={signup.contactName} onChange={e => setField('contactName', e.target.value)} />
            <input placeholder={signup.merchantType === 'institutional' ? 'Company / Business Name' : 'Brand / Store Name'} style={S.input} required
              value={signup.businessName} onChange={e => setField('businessName', e.target.value)} />
            <input type="email" placeholder="Business Email" style={S.input} required
              value={signup.email} onChange={e => setField('email', e.target.value)} autoComplete="email" />
            <input type="tel" placeholder="Phone Number" style={S.input} required
              value={signup.phone} onChange={e => setField('phone', e.target.value)} />
            <select value={signup.gender} onChange={e => setField('gender', e.target.value)} style={S.select} required>
              <option value="" disabled>Select Gender</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
              <option value="Prefer not to say">Prefer not to say</option>
            </select>
            <input placeholder="Business Address" style={S.input} required
              value={signup.address} onChange={e => setField('address', e.target.value)} />
            <input placeholder="City" style={S.input} required
              value={signup.city} onChange={e => setField('city', e.target.value)} />
            <input placeholder="CAC / Registration Number (optional)" style={S.input}
              value={signup.cacNumber} onChange={e => setField('cacNumber', e.target.value)} />

            {/* Bank Details */}
            <SectionLabel>SETTLEMENT BANK DETAILS</SectionLabel>
            <p style={{ color: '#555', fontSize: '0.72rem', margin: '-8px 0 4px' }}>Your earnings will be paid here automatically.</p>
            <input placeholder="Account Number (10 digits)" style={S.input} required maxLength={10}
              value={signup.bankAccountNumber} onChange={e => setField('bankAccountNumber', e.target.value)} />
            <select value={signup.bankCode} onChange={handleBankSelect} style={S.select} required>
              <option value="" disabled>Select Your Bank</option>
              {NIGERIAN_BANKS.map(b => (
                <option key={b.code} value={b.code}>{b.name}</option>
              ))}
            </select>
            {signup.bankCode === '999' && (
              <input placeholder="Enter Bank Name Manually" style={S.input}
                value={signup.bankName} onChange={e => setField('bankName', e.target.value)} />
            )}

            {/* Documents */}
            <SectionLabel>DOCUMENTS & BRANDING</SectionLabel>
            <FileUploadBtn file={regDoc} onChange={setRegDoc} accept=".pdf,image/*"
              label="📄 Upload Business Reg. Doc (PDF or Image)" required />
            <FileUploadBtn file={logoFile} onChange={setLogoFile} accept="image/*"
              label="🖼️ Upload Logo / Profile Photo (Optional)" />

            {/* Password */}
            <SectionLabel>CREATE PASSWORD</SectionLabel>
            <input type="password" placeholder="Password (min 8 characters)" style={S.input} required minLength={8}
              value={signup.password} onChange={e => setField('password', e.target.value)} autoComplete="new-password" />
            <input type="password" placeholder="Confirm Password" style={S.input} required
              value={signup.confirmPassword} onChange={e => setField('confirmPassword', e.target.value)} autoComplete="new-password" />

            {/* Fee Info Box */}
            <div style={S.feeInfoBox}>
              <p style={{ margin: 0, fontSize: '0.78rem', color: '#ccc', lineHeight: '1.6' }}>
                💡 <strong style={{ color: GOLD }}>Platform Fee:</strong> A 10% fee is automatically added to all your listed prices.
                Buyers see your base price + 10%. You receive 100% of your base price.
              </p>
            </div>

            {/* Terms */}
            <div style={S.checkRow}>
              <input type="checkbox" checked={termsAccepted} onChange={e => setTermsAccepted(e.target.checked)}
                style={{ width: '16px', height: '16px', flexShrink: 0, marginTop: '2px' }} />
              <span style={{ color: '#888', fontSize: '0.78rem', lineHeight: '1.5' }}>
                I agree to the{' '}
                <span onClick={() => setShowTerms(true)} style={{ color: GOLD, cursor: 'pointer', textDecoration: 'underline' }}>
                  Merchant Terms & Agreement
                </span>{' '}
                including the 10% platform fee on all items listed.
              </span>
            </div>

            {uploadProgress && (
              <p style={{ color: GOLD, fontSize: '0.78rem', textAlign: 'center', margin: '0' }}>{uploadProgress}</p>
            )}

            <button type="submit" style={{ ...S.goldBtn, opacity: (loading || !termsAccepted) ? 0.6 : 1 }}
              disabled={loading || !termsAccepted}>
              {loading ? 'SUBMITTING APPLICATION...' : 'SUBMIT APPLICATION'}
            </button>
            <Divider />
            <button type="button" onClick={() => handleGoogle(true)} style={S.googleBtn}>
              <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="G" style={{ width: '18px', marginRight: '8px' }} />
              Sign Up with Google
            </button>
            <p style={{ color: '#444', fontSize: '0.75rem', textAlign: 'center', marginTop: '10px' }}>
              Already registered?{' '}
              <span onClick={() => setTab('login')} style={{ color: GOLD, cursor: 'pointer' }}>Sign in</span>
            </p>
          </form>
        )}
      </Overlay>

      {/* ── MERCHANT TERMS MODAL ── */}
      {showTerms && (
        <div style={{ ...S.overlayBase, zIndex: 100002 }} onClick={() => setShowTerms(false)}>
          <div style={{ ...S.modalBase, maxHeight: '82vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <h2 style={S.goldTitle}>MERCHANT TERMS</h2>
            <p style={{ color: '#444', fontSize: '0.65rem', letterSpacing: '2px', marginBottom: '20px' }}>
              ESDRAS MARKETPLACE — VERSION 2026 v1
            </p>
            <div style={{ color: '#aaa', fontSize: '0.84rem', lineHeight: '1.75' }}>
              <h4 style={{ color: GOLD, marginBottom: '5px' }}>1. PLATFORM FEE</h4>
              <p>A 10% platform fee is added to every product price you submit. Buyers pay your base price plus 10%. You receive 100% of your stated base price. The 10% is automatically remitted to Esdras Technologies Inc.</p>
              <h4 style={{ color: GOLD, marginBottom: '5px' }}>2. DELIVERY</h4>
              <p>Esdras Marketplace currently supports deliveries within Lagos at a flat rate of ₦2,500 per order. Deliveries outside Lagos incur a flat rate of ₦10,000 per order. Delivery fees are managed by Esdras and are not part of the merchant payout.</p>
              <h4 style={{ color: GOLD, marginBottom: '5px' }}>3. PAYMENTS & PAYOUTS</h4>
              <p>All NGN payments are processed via Monnify. International payments are processed via Stripe. Your earnings (base price) are credited to your Merchant Wallet upon confirmed payment. Withdrawals are transferred to your registered bank account via Monnify disbursement.</p>
              <h4 style={{ color: GOLD, marginBottom: '5px' }}>4. PRODUCT LISTING & APPROVAL</h4>
              <p>All products require admin approval before going live on the Esdras homepage. Products must include a valid NAFDAC registration number. Listing counterfeit, expired, or harmful products will result in immediate account suspension.</p>
              <h4 style={{ color: GOLD, marginBottom: '5px' }}>5. MERCHANT CONDUCT</h4>
              <p>Merchants must maintain accurate stock quantities and ensure product descriptions are truthful. Esdras reserves the right to remove products or suspend merchant accounts for violations of these terms without prior notice.</p>
              <h4 style={{ color: GOLD, marginBottom: '5px' }}>6. LIABILITY</h4>
              <p>Esdras operates as a marketplace facilitator. Merchants are solely responsible for product quality, accuracy, and authenticity. Esdras is not liable for product defects, buyer disputes, or delivery delays beyond its designated logistics process.</p>
              <h4 style={{ color: GOLD, marginBottom: '5px' }}>7. DATA & PRIVACY</h4>
              <p>Your business data, uploaded documents, and product information are stored securely on Google Firebase (Google Cloud) with industry-standard encryption. Esdras will not sell or share your data with third parties outside of payment processing requirements.</p>
              <h4 style={{ color: GOLD, marginBottom: '5px' }}>8. ACCOUNT TERMINATION</h4>
              <p>Esdras reserves the right to terminate any merchant account that violates these terms, engages in fraudulent activity, or harms the reputation of the Esdras platform. Outstanding wallet balances will be disbursed minus any applicable penalties.</p>
            </div>
            <button onClick={() => { setTermsAccepted(true); setShowTerms(false); }} style={{ ...S.goldBtn, marginTop: '20px' }}>
              I AGREE & CONTINUE
            </button>
            <button onClick={() => setShowTerms(false)}
              style={{ background: 'transparent', color: '#555', border: 'none', cursor: 'pointer', marginTop: '8px', width: '100%', padding: '10px' }}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </>
  );
}

// ─── SUB-COMPONENTS ──────────────────────────────────────────────────────────
function Overlay({ children, onClose }) {
  return (
    <div style={S.overlayBase} onClick={onClose}>
      <div style={{ ...S.modalBase, maxHeight: '92vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <p style={{ color: GOLD, fontSize: '0.62rem', letterSpacing: '2.5px', fontWeight: '900', margin: '12px 0 4px' }}>
      {children}
    </p>
  );
}

function Divider() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '4px 0' }}>
      <div style={{ flex: 1, height: '1px', background: '#1a2631' }} />
      <span style={{ color: '#333', fontSize: '0.7rem' }}>OR</span>
      <div style={{ flex: 1, height: '1px', background: '#1a2631' }} />
    </div>
  );
}

function FileUploadBtn({ file, onChange, accept, label, required }) {
  return (
    <label style={{
      display: 'block', background: CARD_BG,
      border: `1px dashed ${file ? GOLD : '#2a3641'}`,
      borderRadius: '10px', padding: '13px 16px',
      color: file ? GOLD : '#555', fontSize: '0.84rem',
      cursor: 'pointer', textAlign: 'center', transition: 'all 0.2s',
    }}>
      {file ? `✅ ${file.name}` : label}{required && !file && <span style={{ color: '#ff4d4d' }}> *</span>}
      <input type="file" accept={accept} style={{ display: 'none' }} onChange={e => onChange(e.target.files[0])} />
    </label>
  );
}

// ─── STYLES ──────────────────────────────────────────────────────────────────
const S = {
  overlayBase: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)',
    zIndex: 100000, display: 'flex', alignItems: 'center',
    justifyContent: 'center', padding: '16px',
  },
  modalBase: {
    background: DEEP_BLACK, border: `1px solid ${GOLD}33`,
    borderRadius: '24px', padding: '32px 28px',
    width: '100%', maxWidth: '468px',
    fontFamily: "'Montserrat', 'Inter', sans-serif", color: 'white',
    boxShadow: `0 30px 80px rgba(0,0,0,0.8), 0 0 0 1px ${GOLD}11`,
  },
  goldTitle: {
    color: GOLD, letterSpacing: '2.5px', margin: '0 0 4px', fontSize: '1.25rem', fontWeight: '900',
  },
  mutedText: {
    color: '#888', lineHeight: '1.7', maxWidth: '320px', margin: '15px auto 20px',
    fontSize: '0.88rem', textAlign: 'center',
  },
  tabRow: {
    display: 'flex', background: '#060c11', borderRadius: '12px',
    padding: '4px', marginBottom: '22px', border: `1px solid ${BORDER}`,
  },
  tabActive: {
    flex: 1, padding: '11px', background: GOLD, color: 'black',
    border: 'none', borderRadius: '9px', fontWeight: '900',
    fontSize: '0.72rem', letterSpacing: '1.5px', cursor: 'pointer',
  },
  tabInactive: {
    flex: 1, padding: '11px', background: 'transparent', color: '#555',
    border: 'none', borderRadius: '9px', fontWeight: '700',
    fontSize: '0.72rem', letterSpacing: '1.5px', cursor: 'pointer',
  },
  form: { display: 'flex', flexDirection: 'column', gap: '10px' },
  input: {
    background: CARD_BG, border: `1px solid ${BORDER}`, padding: '13px 15px',
    borderRadius: '10px', color: 'white', fontSize: '0.88rem', outline: 'none',
    width: '100%', boxSizing: 'border-box', fontFamily: 'inherit',
  },
  select: {
    background: CARD_BG, border: `1px solid ${BORDER}`, padding: '13px 15px',
    borderRadius: '10px', color: 'white', fontSize: '0.88rem', outline: 'none',
    width: '100%', boxSizing: 'border-box', appearance: 'none', fontFamily: 'inherit',
  },
  toggleRow: { display: 'flex', gap: '8px' },
  toggleActive: {
    flex: 1, padding: '10px 8px', background: GOLD, color: 'black',
    border: 'none', borderRadius: '8px', fontWeight: '800',
    fontSize: '0.7rem', letterSpacing: '0.5px', cursor: 'pointer',
  },
  toggleInactive: {
    flex: 1, padding: '10px 8px', background: CARD_BG, color: '#555',
    border: `1px solid ${BORDER}`, borderRadius: '8px', fontWeight: '700',
    fontSize: '0.7rem', letterSpacing: '0.5px', cursor: 'pointer',
  },
  feeInfoBox: {
    background: `rgba(184,134,11,0.07)`, border: `1px solid ${GOLD}33`,
    borderRadius: '10px', padding: '12px 14px', marginTop: '4px',
  },
  checkRow: {
    display: 'flex', gap: '10px', alignItems: 'flex-start',
    background: CARD_BG, padding: '12px', borderRadius: '10px',
    border: `1px solid ${BORDER}`,
  },
  goldBtn: {
    background: GOLD, color: 'black', padding: '15px', borderRadius: '50px',
    fontWeight: '900', border: 'none', cursor: 'pointer', fontSize: '0.88rem',
    letterSpacing: '1px', width: '100%', marginTop: '4px', fontFamily: 'inherit',
  },
  googleBtn: {
    background: 'white', color: '#333', padding: '13px', borderRadius: '50px',
    fontWeight: '700', border: 'none', cursor: 'pointer', fontSize: '0.84rem',
    display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%',
    fontFamily: 'inherit',
  },
};
